import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, Save, Building2, Mail, Phone, MapPin, Hash, 
  Loader2, AlertCircle, CheckCircle 
} from 'lucide-react';
import { Client } from '@/types/Offer';
import { Button } from '@/components/ui/button';
import { useClients } from '@/hooks/useClients';

interface ClientEditModalProps {
  client: Client & { id: string };
  isOpen: boolean;
  onClose: () => void;
  onSave?: (client: Client) => void;
}

export const ClientEditModal: React.FC<ClientEditModalProps> = ({ 
  client, 
  isOpen, 
  onClose,
  onSave 
}) => {
  const { updateClient } = useClients();
  const [editedClient, setEditedClient] = useState<Client>(client);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setEditedClient(client);
    setError(null);
    setSuccess(false);
  }, [client, isOpen]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const updated = await updateClient(client.id, editedClient);
      if (updated) {
        setSuccess(true);
        if (onSave) onSave(editedClient);
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        setError('Nie udało się zaktualizować danych klienta');
      }
    } catch (err) {
      setError('Wystąpił błąd podczas zapisywania');
      console.error('Error updating client:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof Client, value: string) => {
    setEditedClient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-zinc-800 rounded-2xl w-full max-w-lg p-6 border border-zinc-700"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Edytuj dane klienta</h2>
            </div>
            <motion.button
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-all"
            >
              <X className="w-5 h-5 text-gray-400" />
            </motion.button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <Building2 className="w-4 h-4 inline mr-1" />
                Nazwa firmy
              </label>
              <input
                type="text"
                value={editedClient.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  <Hash className="w-4 h-4 inline mr-1" />
                  NIP
                </label>
                <input
                  type="text"
                  value={editedClient.nip}
                  onChange={(e) => handleChange('nip', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  REGON
                </label>
                <input
                  type="text"
                  value={editedClient.regon || ''}
                  onChange={(e) => handleChange('regon', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Adres
              </label>
              <input
                type="text"
                value={editedClient.address}
                onChange={(e) => handleChange('address', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email
              </label>
              <input
                type="email"
                value={editedClient.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Telefon
              </label>
              <input
                type="tel"
                value={editedClient.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
              />
            </div>

            {/* Lokalizacja */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Województwo
                </label>
                <input
                  type="text"
                  value={editedClient.wojewodztwo || ''}
                  onChange={(e) => handleChange('wojewodztwo', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Powiat
                </label>
                <input
                  type="text"
                  value={editedClient.powiat || ''}
                  onChange={(e) => handleChange('powiat', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Gmina
                </label>
                <input
                  type="text"
                  value={editedClient.gmina || ''}
                  onChange={(e) => handleChange('gmina', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-700/50 rounded-xl text-white border border-zinc-600 focus:border-purple-500 focus:outline-none transition-all"
                />
              </div>
            </div>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 bg-red-900/20 border border-red-500/30 rounded-xl flex items-center gap-2 text-red-400"
            >
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </motion.div>
          )}

          {success && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 bg-emerald-900/20 border border-emerald-500/30 rounded-xl flex items-center gap-2 text-emerald-400"
            >
              <CheckCircle className="w-5 h-5" />
              <span>Dane klienta zostały zaktualizowane</span>
            </motion.div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <Button
              onClick={onClose}
              variant="secondary"
              className="flex-1"
              disabled={saving}
            >
              Anuluj
            </Button>
            <Button
              onClick={handleSave}
              variant="primary"
              className="flex-1"
              disabled={saving || !editedClient.name || !editedClient.nip}
            >
              {saving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Zapisywanie...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Zapisz zmiany
                </>
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
